<template>
    <div class="template-toolbar">
        <div class="toolbar-row">
            <button
                type="button"
                class="tb-action tb-action--text"
                @click="emit('addText')"
            >
                <i class="pi pi-pencil tb-action__icon"></i>
                <span class="tb-action__label">Текст</span>
            </button>

            <label class="tb-action tb-action--image">
                <i class="pi pi-image tb-action__icon"></i>
                <span class="tb-action__label">Картинка</span>
                <input
                    type="file"
                    class="tb-action__input"
                    accept="image/*"
                    @change="onImageChange"
                />
            </label>

            <label class="tb-action tb-action--bg">
                <i class="pi pi-palette tb-action__icon"></i>
                <span class="tb-action__label">Изменить фон</span>
                <input
                    type="file"
                    class="tb-action__input"
                    accept="image/*"
                    @change="onBackgroundChange"
                />
            </label>

            <div class="tb-separator"></div>

            <button
                type="button"
                class="tb-icon-action"
                :disabled="!canUndo"
                @click="emit('undo')"
                v-tooltip.bottom="'Отменить (Ctrl+Z)'"
            >
                <i class="pi pi-undo"></i>
            </button>

            <button
                type="button"
                class="tb-icon-action"
                :disabled="!canRedo"
                @click="emit('redo')"
                v-tooltip.bottom="'Вернуть (Ctrl+Y)'"
            >
                <i class="pi pi-replay"></i>
            </button>

            <div class="tb-separator"></div>

            <button
                type="button"
                class="tb-action tb-action--preview"
                :class="{ 'is-active': previewWithData }"
                :disabled="!previewAvailable"
                @click="togglePreview"
                v-tooltip.bottom="previewAvailable ? (previewWithData ? 'Показывать шаблон' : 'Показать с данными') : 'Нет данных для подстановки'"
            >
                <i :class="['tb-action__icon', previewWithData ? 'pi pi-eye-slash' : 'pi pi-eye']"></i>
                <span class="tb-action__label">С данными</span>
            </button>

            <PrimeDropdown
                v-model="previewRowModel"
                :options="previewOptions"
                optionLabel="label"
                optionValue="value"
                placeholder="Строка"
                class="tb-preview-row"
                :disabled="!previewWithData || !previewAvailable"
            />

            <div class="tb-separator"></div>

            <div :class="['tb-text-controls', { disabled: !isText }]">
                <PrimeDropdown
                    v-model="fontFamily"
                    :options="fontFamilies"
                    placeholder="Шрифт"
                    class="tb-font-family"
                    :disabled="!isText"
                />

                <PrimeDropdown
                    v-model="fontSize"
                    :options="fontSizes"
                    placeholder="Размер"
                    class="tb-font-size"
                    :disabled="!isText"
                />

                <div class="tb-separator"></div>

                <SelectButton
                    v-model="fontStyle"
                    :options="fontStyleOptions"
                    multiple
                    optionLabel="label"
                    optionValue="value"
                    optionDisabled="disable"
                    dataKey="value"
                    class="tb-compact-select tb-font-style-select"
                    :disabled="!isText"
                >
                    <template #option="slotProps">
                        <span class="tb-font-style-option" v-html="slotProps.option.label" :style="slotProps.option.style"></span>
                    </template>
                </SelectButton>

                <div class="tb-separator"></div>

                <SelectButton
                    v-model="align"
                    :options="justifyOptions"
                    optionLabel="value"
                    optionValue="value"
                    dataKey="value"
                    class="tb-compact-select"
                    :disabled="!isText"
                >
                    <template #option="slotProps">
                        <i :class="slotProps.option.icon"></i>
                    </template>
                </SelectButton>

                <div class="tb-separator"></div>

                <div class="tb-color-group">
                    <label class="tb-label">Цвет</label>
                    <ColorPicker v-model="fontColor" :disabled="!isText" />
                </div>

                <div class="tb-color-group">
                    <label class="tb-label">Фон</label>
                    <ColorPicker v-model="bgColor" :disabled="!isText || !hasBg" />
                    <PrimeButton
                        :icon="hasBg ? 'pi pi-ban' : 'pi pi-check'"
                        :class="['p-button-sm p-button-text p-button-rounded', hasBg ? 'p-button-secondary' : 'p-button-success']"
                        :disabled="!isText"
                        @click="toggleBg"
                        v-tooltip.bottom="hasBg ? 'Убрать фон' : 'Включить фон'"
                        style="width: 24px; height: 24px;"
                    />
                </div>

                <div class="tb-separator"></div>

                <div class="tb-slider-group">
                    <label class="tb-label">Высота</label>
                    <PrimeSlider
                        v-model="lineHeight"
                        :min="3"
                        :max="60"
                        :disabled="!isText"
                        class="tb-slider"
                    />
                    <span class="tb-slider-value">{{ isText ? theBlock.height : '–' }}</span>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, ref, defineProps, defineEmits } from "vue";

const props = defineProps({
    modelValue: { type: Object, default: null },
    blockIndex: { type: Number, default: -1 },
    fonts: { type: Array, default: () => [] },
    canUndo: { type: Boolean, default: false },
    canRedo: { type: Boolean, default: false },
    previewWithData: { type: Boolean, default: false },
    previewRowKey: { type: String, default: null },
    previewOptions: { type: Array, default: () => [] },
});

const emit = defineEmits([
    'addText',
    'addImage',
    'uploadBackground',
    'undo',
    'redo',
    'beforeBlockChange',
    'update:modelValue',
    'update:previewWithData',
    'update:previewRowKey',
]);

const previewAvailable = computed(() => props.previewOptions.length > 0);

const togglePreview = () => {
    if (!previewAvailable.value) return;
    emit('update:previewWithData', !props.previewWithData);
};

const previewRowModel = computed({
    get: () => props.previewRowKey,
    set: (value) => emit('update:previewRowKey', value),
});

const theBlock = computed({
    get: () => props.modelValue,
    set: (value) => emit('update:modelValue', value),
});

const isText = computed(() => props.modelValue && props.modelValue.type === 'text');

const updateTextBlock = (changeKey, updater) => {
    if (!isText.value) return;
    emit('beforeBlockChange', changeKey);
    updater(theBlock.value);
    emit('update:modelValue', theBlock.value);
};

const fontFamily = computed({
    get: () => isText.value ? props.modelValue.font.family : null,
    set: (value) => updateTextBlock('font.family', block => block.font.family = value)
});

const fontSize = computed({
    get: () => isText.value ? props.modelValue.font.size : null,
    set: (value) => updateTextBlock('font.size', block => block.font.size = value)
});

const align = computed({
    get: () => isText.value ? props.modelValue.align : null,
    set: (value) => updateTextBlock('align', block => block.align = value)
});

const lineHeight = computed({
    get: () => isText.value ? props.modelValue.height : 10,
    set: (value) => updateTextBlock('height', block => block.height = value)
});

const fontColor = computed({
    get: () => isText.value ? props.modelValue.font.color.replace('#', '') : '000000',
    set: (value) => updateTextBlock('font.color', block => block.font.color = '#' + value)
});

const hasBg = computed(() => isText.value && props.modelValue.background !== '');

const toggleBg = () => {
    if (!isText.value) return;
    updateTextBlock('background.toggle', block => {
        block.background = hasBg.value ? '' : '#ffffff';
    });
};

const bgColor = computed({
    get: () => isText.value && props.modelValue.background ? props.modelValue.background.replace('#', '') : 'ffffff',
    set: (value) => updateTextBlock('background.color', block => block.background = '#' + value)
});

const normalizeFontStyle = (style = '') => {
    const chars = Array.from(new Set(style.toLowerCase().split('').filter(char => ['b', 'i'].includes(char))));
    if (chars.length === 2) return 'bi';
    return chars.join('');
};

const fontStyle = computed({
    get: () => isText.value ? normalizeFontStyle(props.modelValue.font.style).split('') : [],
    set: (value) => updateTextBlock('font.style', block => block.font.style = normalizeFontStyle(value.join('')))
});

const fontFamilies = computed(() => props.fonts.map(f => f.family));

const fontSizes = ref([8, 10, 11, 12, 13, 15, 16, 18, 20, 24, 28, 30, 32, 36, 38, 40, 42, 50]);

const justifyOptions = ref([
    { icon: 'pi pi-align-left', value: 'L' },
    { icon: 'pi pi-align-center', value: 'C' },
    { icon: 'pi pi-align-right', value: 'R' },
]);

const selectedFont = computed(() => {
    if (!isText.value) return '';
    let selected = props.fonts.filter(f => f.family === props.modelValue.font.family);
    if (!selected.length) return '';
    selected = selected[0];
    return {
        family: props.modelValue.font.family,
        hasBoldSignature: selected.styles.indexOf('b') > -1,
        hasItalicSignature: selected.styles.indexOf('i') > -1,
        hasBoldItalicSignature: selected.styles.indexOf('bi') > -1,
    };
});

const boldAvailable = computed(() => {
    if (selectedFont.value === '') return false;
    let style = normalizeFontStyle(props.modelValue.font.style);
    return (selectedFont.value.hasBoldSignature && (style === "" || style === "b" || style === "bi"))
        || (selectedFont.value.hasBoldItalicSignature && (style === "i" || style === "bi"));
});

const italicAvailable = computed(() => {
    if (selectedFont.value === '') return false;
    let style = normalizeFontStyle(props.modelValue.font.style);
    return (selectedFont.value.hasItalicSignature && (style === "" || style === "i" || style === "bi"))
        || (selectedFont.value.hasBoldItalicSignature && (style === "b" || style === "bi"));
});

const fontStyleOptions = computed(() => [
    { label: 'B', style: 'font-weight: bold; line-height: 16px', value: 'b', disable: !isText.value || !boldAvailable.value },
    { label: 'I', style: 'font-weight: bold; font-style: italic; line-height: 16px', value: 'i', disable: !isText.value || !italicAvailable.value },
]);

const onImageChange = (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length) emit('addImage', { files });
    event.target.value = '';
};

const onBackgroundChange = (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length) emit('uploadBackground', { files });
    event.target.value = '';
};
</script>

<style scoped lang="scss">
.template-toolbar {
    background: #fff;
    border-bottom: 1px solid #e0e0e0;
}

.toolbar-row {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 12px;
    flex-wrap: wrap;
}

.tb-text-controls {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
    transition: opacity 0.2s;

    &.disabled {
        opacity: 0.45;
        pointer-events: none;
    }
}

.tb-separator {
    width: 1px;
    height: 24px;
    background: #ddd;
    flex-shrink: 0;
}

.tb-font-family {
    width: 150px;
    :deep(.p-dropdown-label) {
        font-size: 0.8rem;
        padding: 0.3rem 0.5rem;
    }
}

.tb-font-size {
    width: 68px;
    :deep(.p-dropdown-label) {
        font-size: 0.8rem;
        padding: 0.3rem 0.5rem;
    }
}

.tb-compact-select {
    :deep(.p-button) {
        padding: 0.3rem 0.5rem;
        font-size: 0.8rem;
    }
}

.tb-font-style-select {
    :deep(.p-button) {
        min-width: 2.35rem;
        justify-content: center;
    }
}

.tb-font-style-option {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 18px;
}

.tb-action {
    --tb-accent: #6366f1;
    --tb-accent-soft: #eef2ff;
    --tb-accent-strong: #4338ca;

    position: relative;
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 0.9rem;
    border-radius: 10px;
    border: 1px solid transparent;
    background: var(--tb-accent-soft);
    color: var(--tb-accent-strong);
    font-weight: 600;
    font-size: 0.8rem;
    letter-spacing: 0.01em;
    line-height: 1;
    cursor: pointer;
    user-select: none;
    transition: background 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;
}

.tb-icon-action {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    border-radius: 8px;
    border: 1px solid #d4d4d8;
    background: #fff;
    color: #334155;
    cursor: pointer;
    transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;

    &:hover:not(:disabled) {
        background: #f8fafc;
        border-color: #94a3b8;
        color: #0f172a;
    }

    &:disabled {
        opacity: 0.4;
        cursor: not-allowed;
    }
}

.tb-action__icon {
    font-size: 0.95rem;
    color: var(--tb-accent);
    transition: color 0.2s ease, transform 0.2s ease;
}

.tb-action__label {
    white-space: nowrap;
}

.tb-action__input {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    border: 0;
}

.tb-action:hover {
    background: var(--tb-accent-hover);
    border-color: var(--tb-accent);
}

.tb-action:active {
    box-shadow: inset 0 1px 2px rgba(15, 23, 42, 0.08);
}

.tb-action:focus-visible {
    outline: 2px solid var(--tb-accent);
    outline-offset: 2px;
}

.tb-action--text {
    --tb-accent: #6366f1;
    --tb-accent-soft: #eef2ff;
    --tb-accent-hover: #e0e7ff;
    --tb-accent-strong: #4338ca;
}

.tb-action--image {
    --tb-accent: #10b981;
    --tb-accent-soft: #ecfdf5;
    --tb-accent-hover: #d1fae5;
    --tb-accent-strong: #047857;
}

.tb-action--bg {
    --tb-accent: #f59e0b;
    --tb-accent-soft: #fffbeb;
    --tb-accent-hover: #fef3c7;
    --tb-accent-strong: #b45309;
}

.tb-action--preview {
    --tb-accent: #64748b;
    --tb-accent-soft: #f1f5f9;
    --tb-accent-hover: #e2e8f0;
    --tb-accent-strong: #334155;

    &.is-active {
        --tb-accent: #0ea5e9;
        --tb-accent-soft: #e0f2fe;
        --tb-accent-hover: #bae6fd;
        --tb-accent-strong: #075985;
        border-color: var(--tb-accent);
    }

    &[disabled] {
        opacity: 0.5;
        cursor: not-allowed;
    }
}

.tb-preview-row {
    width: 160px;
    :deep(.p-dropdown-label) {
        font-size: 0.8rem;
        padding: 0.3rem 0.5rem;
    }
}

.tb-color-group {
    display: flex;
    align-items: center;
    gap: 4px;

    :deep(.p-colorpicker-preview) {
        width: 22px;
        height: 22px;
        border-radius: 4px;
    }
}

.tb-slider-group {
    display: flex;
    align-items: center;
    gap: 6px;
}

.tb-slider {
    width: 80px;
}

.tb-slider-value {
    font-size: 0.75rem;
    color: #666;
    min-width: 20px;
    text-align: center;
}

.tb-label {
    font-size: 0.75rem;
    color: #888;
    white-space: nowrap;
}

.tb-unit {
    font-size: 0.7rem;
    color: #999;
}
</style>
